import { useIsMobile } from '@/hooks/use-mobile';
import { useMutation } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

import { useEffect, useMemo, useRef, useState } from 'react';

import { DialogCloseButton } from '@/components/dialog-close';
import { InfoIcon } from '@/components/icons/svgs';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

import { Tooltip } from '@/shared/components';
import ChatMessageInput from '@/shared/components/chat-message-input/chat-message-input';
import ConfirmDialog from '@/shared/components/confirm-dialog';
import { toast } from '@/shared/components/toast';
import { COPY } from '@/shared/constants/copies';
import {
	BUILT_IN_QUICK_REPLIES,
	getQuickReplyVariables,
	isNonDeletableQuickReply,
} from '@/shared/constants/quick-replies';
import { getMessageVariableTooltip } from '@/shared/utils/message-variables';
import { IQuickReply } from '@/shared/services/forms/forms.types';

import { cn } from '@/utils/styles';

import { QuickRepliesModalProps } from './quick-replies-modal.types';

// Shared template editor content - used by both desktop and mobile
interface TemplateEditorContentProps {
	editData: { name: string; text: string };
	selectedReply: IQuickReply | null;
	error: string | null;
	isPending: boolean;
	isDeletingTemplate: boolean;
	isMobile?: boolean;
	onUpdateField: (field: 'name' | 'text', value: string) => void;
	onSave: () => void;
	onDelete: () => void;
}

function TemplateEditorContent({
	editData,
	selectedReply,
	error,
	isPending,
	isDeletingTemplate,
	isMobile = false,
	onUpdateField,
	onSave,
	onDelete,
}: TemplateEditorContentProps) {
	const shortNameInputRef = useRef<HTMLInputElement>(null);

	const variables = getQuickReplyVariables(selectedReply?.name);
	const builtIn = selectedReply ? BUILT_IN_QUICK_REPLIES[selectedReply.name] : undefined;

	return (
		<div className={cn('flex min-h-0 flex-1 flex-col', isMobile ? 'p-4' : 'p-6 pt-0')}>
			<ScrollArea className="flex-1">
				<div className={cn(isMobile ? 'space-y-6' : 'space-y-8 px-4')}>
					{/* Short Name Field */}
					<div className="space-y-2">
						<label
							htmlFor="quick-reply-name"
							className={cn('font-semibold text-primary', isMobile ? 'text-sm' : 'text-base')}
						>
							Short name
							<span
								className={cn('block font-normal text-secondary', isMobile ? 'text-xs' : 'text-sm')}
							>
								{COPY.messaging.quickReplyNameHelp}
							</span>
						</label>
						<Input
							ref={shortNameInputRef}
							id="quick-reply-name"
							value={editData.name}
							onChange={(e) => onUpdateField('name', e.target.value)}
							placeholder="Enter short name..."
							disabled={selectedReply?.isDefault}
							className={cn(
								'bg-white',
								isMobile ? 'w-full' : 'w-60',
								selectedReply?.isDefault && 'cursor-not-allowed opacity-60'
							)}
						/>
					</div>

					{/* Message Field */}
					<div className="flex-1 space-y-2">
						<label
							htmlFor="quick-reply-text"
							className={cn('font-semibold text-primary', isMobile ? 'text-sm' : 'text-base')}
						>
							Message
							<span
								className={cn('block font-normal text-secondary', isMobile ? 'text-xs' : 'text-sm')}
							>
								{COPY.messaging.variablesHint}
							</span>
						</label>
						<ChatMessageInput
							value={editData.text}
							onChange={(newValue) => onUpdateField('text', newValue)}
							placeholder="Enter message..."
							enableVariableList
							variables={variables}
							getVariableTooltip={getMessageVariableTooltip}
							contentClassName={isMobile ? 'min-h-[100px]' : 'min-h-[128px]'}
						/>
					</div>

					{/* Default Template Info */}
					{builtIn && (
						<div className="rounded-md bg-amber-50 p-3 text-sm font-medium text-amber-900">
							<div className="flex items-center gap-2">
								<svg
									width="16px"
									height="16px"
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="shrink-0"
								>
									<path
										d="M20 6L9 17L4 12"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
								<span>{COPY.messaging.quickReplyDefaultSuggestion(builtIn.hint)}</span>
							</div>
						</div>
					)}

					{/* Error Display */}
					{error && (
						<div className="rounded-md bg-red-50 p-3">
							<p className="text-sm font-medium text-red-900">{error}</p>
						</div>
					)}
				</div>
			</ScrollArea>

			{/* Action Buttons */}
			<div className="flex items-center justify-end gap-2 bg-white pt-4">
				{selectedReply && (
					<Tooltip text={selectedReply.isDefault ? COPY.messaging.quickReplyCannotDelete : ''}>
						<div>
							<Button
								variant="light"
								onClick={onDelete}
								disabled={selectedReply.isDefault || isDeletingTemplate}
								className="flex items-center gap-2"
							>
								Delete
							</Button>
						</div>
					</Tooltip>
				)}
				<Button
					onClick={onSave}
					disabledWithSpinner={isPending && !isDeletingTemplate}
				>
					Save
				</Button>
			</div>
		</div>
	);
}

// Desktop sidebar component
interface DesktopSidebarProps {
	quickReplies: IQuickReply[];
	selectedReply: IQuickReply | null;
	isOnAddNewTemplate: boolean;
	onSelectReply: (reply: IQuickReply) => void;
	onAddNew: () => void;
	infoTooltipText: string;
	isWorkspaceLevel?: boolean;
}

function DesktopSidebar({
	quickReplies,
	selectedReply,
	isOnAddNewTemplate,
	onSelectReply,
	onAddNew,
	infoTooltipText,
	isWorkspaceLevel = false,
}: DesktopSidebarProps) {
	return (
		<div className="flex h-full w-64 flex-col rounded-l-lg bg-accent p-6">
			<div className="mb-6 flex items-center gap-2 text-base font-medium text-primary">
				{isWorkspaceLevel ? 'Default shortcuts' : 'Reply shortcuts'}
				<Tooltip text={infoTooltipText}>
					<span>
						<InfoIcon className="size-4" />
					</span>
				</Tooltip>
			</div>
			<div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
				{quickReplies.map((reply) => (
					<div
						key={reply.name}
						role="button"
						tabIndex={0}
						className={cn(
							'group relative flex cursor-pointer items-center justify-between rounded px-3 py-2 text-secondary hover:bg-slate-200 hover:text-primary',
							selectedReply?.name === reply.name && 'bg-slate-200 text-primary'
						)}
						onClick={() => onSelectReply(reply)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								onSelectReply(reply);
							}
						}}
					>
						<span className="text-sm font-medium">{reply.name}</span>
					</div>
				))}
			</div>

			<div className="mt-auto w-full border-t border-border pt-2">
				<Tooltip text={isOnAddNewTemplate ? COPY.messaging.quickReplyAlreadyAdding : ''}>
					<span>
						<Button
							variant="light"
							className={cn('w-full', isOnAddNewTemplate && 'opacity-40')}
							onClick={onAddNew}
							disabled={isOnAddNewTemplate}
						>
							<Plus className="size-4" /> Add new template
						</Button>
					</span>
				</Tooltip>
			</div>
		</div>
	);
}

// Mobile horizontal template selector
interface MobileTemplateSelectorProps {
	quickReplies: IQuickReply[];
	selectedReply: IQuickReply | null;
	isOnAddNewTemplate: boolean;
	onSelectReply: (reply: IQuickReply) => void;
	onAddNew: () => void;
}

function MobileTemplateSelector({
	quickReplies,
	selectedReply,
	isOnAddNewTemplate,
	onSelectReply,
	onAddNew,
}: MobileTemplateSelectorProps) {
	return (
		<div className="flex shrink-0 items-center gap-2 border-b border-border bg-accent py-3 pl-4 pr-2">
			{/* Scrollable templates */}
			<ScrollArea className="min-w-0 flex-1">
				<div className="flex gap-2 pb-1">
					{quickReplies.map((reply) => (
						<button
							key={reply.name}
							type="button"
							className={cn(
								'shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
								selectedReply?.name === reply.name
									? 'bg-primary text-white'
									: 'bg-white text-secondary hover:bg-slate-100 hover:text-primary'
							)}
							onClick={() => onSelectReply(reply)}
						>
							{reply.name}
						</button>
					))}
				</div>
				<ScrollBar orientation="horizontal" />
			</ScrollArea>

			{/* Fixed plus button */}
			<button
				type="button"
				className={cn(
					'flex shrink-0 items-center justify-center rounded-full bg-primary p-2 text-white transition-opacity',
					isOnAddNewTemplate && 'opacity-40'
				)}
				onClick={onAddNew}
				disabled={isOnAddNewTemplate}
				aria-label="Add new template"
			>
				<Plus className="size-4" />
			</button>
		</div>
	);
}

function QuickRepliesModal({
	isOpen,
	onClose,
	quickReplies,
	onSaveReplies,
	onUpdateQuickReplies,
	infoTooltipText = COPY.messaging.quickReplyInfoTooltip,
	isWorkspaceLevel = false,
}: QuickRepliesModalProps) {
	const isMobile = useIsMobile();
	const [selectedReply, setSelectedReply] = useState<IQuickReply | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [, setHasEditedMessage] = useState(false);
	const [showConfirmDeleteTemplate, setShowConfirmDeleteTemplate] = useState(false);
	const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);

	const [editData, setEditData] = useState({
		name: '',
		text: '',
	});
	const dialogContentRef = useRef<HTMLDivElement>(null);

	const updatedQuickReplies: IQuickReply[] =
		quickReplies?.map((reply) => ({
			...reply,
			isDefault: isNonDeletableQuickReply(reply.name),
		})) ?? [];

	const updateQuickRepliesMutation = useMutation({
		mutationFn: async (updatedReplies: IQuickReply[]) => {
			const response = await onSaveReplies(
				updatedReplies.map(({ text, name, priority }) => ({
					text,
					name,
					priority,
				}))
			);
			return response.map((reply) => ({
				...reply,
				isDefault: isNonDeletableQuickReply(reply.name),
			}));
		},
		onSuccess: (data, variables) => {
			onUpdateQuickReplies?.(data);

			const action = selectedReply
				? variables.length < updatedQuickReplies.length
					? 'deleted'
					: 'updated'
				: 'added';

			if (action === 'added') {
				setEditData({ name: '', text: '' });
				handleSelectReply(data[data?.length - 1]);
			}

			if (action === 'updated' && selectedReply) {
				const updated = data.find((r) => r.name === editData.name.trim());
				if (updated) handleSelectReply(updated);
			}

			toast({
				title:
					action === 'deleted'
						? 'Template removed'
						: action === 'added'
							? 'Template saved'
							: 'Template saved',
				variant: 'success',
			});
		},
		onError: () => {
			const action = selectedReply
				? updatedQuickReplies.some((r) => r.name === selectedReply.name)
					? 'save changes to'
					: 'remove'
				: 'add';

			const replyName = selectedReply?.name || editData.name;

			toast({
				title: 'Something went wrong',
				description: `Unable to ${action} "${replyName}". Please try again`,
				variant: 'destructive',
			});
		},
	});

	const handleAddQuickReply = () => {
		setSelectedReply(null);
		setEditData({ name: '', text: '' });
		setError(null);
		setHasEditedMessage(false);
	};

	const handleSelectReply = (reply: IQuickReply) => {
		if (!reply) return;
		setSelectedReply(reply);

		setEditData({
			name: reply.name,
			text: reply.text,
		});
		setError(null);
		setHasEditedMessage(true);
	};

	const handleUpdateField = (field: 'name' | 'text', value: string) => {
		if (field === 'name' && selectedReply?.isDefault) return;
		setEditData((prev) => ({
			...prev,
			[field]: value,
		}));
		if (field === 'text') {
			setHasEditedMessage(true);
		}
		setError(null);
	};

	const handleDeleteReply = async () => {
		if (!selectedReply || selectedReply.isDefault) return;
		setIsDeletingTemplate(true);
		try {
			const updatedReplies = updatedQuickReplies.filter(
				(reply: IQuickReply) => reply.name !== selectedReply.name
			);
			await updateQuickRepliesMutation.mutateAsync(updatedReplies);
			setEditData({ name: '', text: '' });
			setSelectedReply(null);
			setIsDeletingTemplate(false);
		} catch (error) {
			setError('Failed to delete template');
			console.error('Failed to delete template:', error);
			setIsDeletingTemplate(false);
		}
	};

	const handleSave = async () => {
		const { name, text } = editData;
		if (!name.trim() || !text.trim()) {
			setError('Please fill in both fields.');
			return;
		}

		try {
			let updatedReplies: IQuickReply[];

			if (selectedReply) {
				updatedReplies = updatedQuickReplies.map((reply: IQuickReply) =>
					reply.name === selectedReply.name
						? {
								...reply,
								text: text.trim(),
								name: selectedReply.isDefault ? reply.name : name.trim(),
							}
						: reply
				);
			} else {
				const isDuplicate = updatedQuickReplies.some(
					(reply: IQuickReply) => reply.name.toLowerCase() === name.trim().toLowerCase()
				);

				if (isDuplicate) {
					setError('A template with this name already exists.');
					return;
				}

				const newReply: IQuickReply = {
					name: name.trim(),
					text: text.trim(),
					priority: Math.max(...updatedQuickReplies.map((r: IQuickReply) => r.priority), 0) + 1,
					isDefault: false,
				};
				updatedReplies = [...updatedQuickReplies, newReply];
			}

			await updateQuickRepliesMutation.mutateAsync(updatedReplies);
		} catch (error) {
			setError('Failed to save template');
			console.error('Failed to save template:', error);
		}
	};

	const isOnAddNewTemplate = useMemo(() => {
		return !selectedReply && !editData.name.trim() && !editData.text.trim();
	}, [selectedReply, editData.name, editData.text]);

	useEffect(() => {
		handleSelectReply(updatedQuickReplies[0]);
	}, []);

	// Shared editor props
	const editorProps: TemplateEditorContentProps = {
		editData,
		selectedReply,
		error,
		isPending: updateQuickRepliesMutation.isPending,
		isDeletingTemplate,
		isMobile,
		onUpdateField: handleUpdateField,
		onSave: handleSave,
		onDelete: () => setShowConfirmDeleteTemplate(true),
	};

	return (
		<>
			<Dialog
				open={isOpen}
				onOpenChange={onClose}
			>
				<DialogContent
					onFocusCapture={(e) => {
						e.stopPropagation();
						e.preventDefault();
					}}
					onKeyDown={(e) => e.stopPropagation()}
					ref={dialogContentRef}
					onOpenAutoFocus={(e) => {
						e.preventDefault();
						dialogContentRef.current?.focus();
					}}
					className={cn(
						'flex flex-col p-0 focus-visible:outline-none',
						isMobile ? 'h-[85vh] w-[calc(100vw-2rem)] max-w-lg gap-0' : 'h-[600px] sm:max-w-[900px]'
					)}
				>
					<DialogDescription className="hidden" />

					{isMobile ? (
						// Mobile Layout
						<>
							{/* Header */}
							<div className="flex shrink-0 items-center justify-between bg-accent px-4 py-3">
								<div className="flex items-center gap-2 text-base font-medium text-primary">
									{isWorkspaceLevel ? 'Default shortcuts' : 'Reply shortcuts'}
									<Tooltip text={infoTooltipText}>
										<span>
											<InfoIcon className="size-4" />
										</span>
									</Tooltip>
								</div>
								<DialogCloseButton />
							</div>

							{/* Horizontal Template Selector */}
							<MobileTemplateSelector
								quickReplies={updatedQuickReplies}
								selectedReply={selectedReply}
								isOnAddNewTemplate={isOnAddNewTemplate}
								onSelectReply={handleSelectReply}
								onAddNew={handleAddQuickReply}
							/>

							{/* Editor Content */}
							<TemplateEditorContent {...editorProps} />
						</>
					) : (
						// Desktop Layout
						<div className="flex h-[calc(100%-1px)] flex-1">
							{/* Left sidebar */}
							<DesktopSidebar
								quickReplies={updatedQuickReplies}
								selectedReply={selectedReply}
								isOnAddNewTemplate={isOnAddNewTemplate}
								onSelectReply={handleSelectReply}
								onAddNew={handleAddQuickReply}
								infoTooltipText={infoTooltipText}
								isWorkspaceLevel={isWorkspaceLevel}
							/>

							{/* Right content */}
							<div className="flex flex-1 flex-col">
								<div className="flex justify-end p-6 pb-0">
									<DialogCloseButton />
								</div>
								<TemplateEditorContent {...editorProps} />
							</div>
						</div>
					)}

					<ConfirmDialog
						isOpen={showConfirmDeleteTemplate}
						onClose={() => setShowConfirmDeleteTemplate(false)}
						onConfirm={handleDeleteReply}
						description={COPY.messaging.quickReplyDeleteConfirm}
						title={COPY.confirms.areYouSure}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}

export default QuickRepliesModal;
